import { kebabCase } from 'change-case'
interface ICreateLocatorsArgs<P> {
  name?: string
  componentLocators: P[]
}

const dataAttr = (attr: string) => `data-${kebabCase(attr)}`
const KEY = dataAttr('testid')

type Suffix = string | number
export type Attribute = string | number | boolean | undefined
export type Attributes = Record<string, Attribute>

interface IGenerateLocatorsDataPayload {
  locator: string
  testIdAttribute: string
  map: Map<string, string>
}

const createGenerateQueryString = ({ locator, testIdAttribute, map }: IGenerateLocatorsDataPayload) => {
  function generateQueryString(): string
  function generateQueryString(suffix: Suffix, additional?: Attributes): string
  function generateQueryString(additional?: Attributes): string

  function generateQueryString(...args: unknown[]) {
    const suffix = typeof args[0] !== 'object' ? (args[0] as Suffix) : undefined

    const additional = (
      typeof args[1] !== 'undefined' ? args[1] : typeof args[0] === 'object' && args[0] !== null ? args[0] : {}
    ) as Attributes

    const base = `[${testIdAttribute}="${toLocator({
      map,
      locator,
      suffix,
    })}"]`

    return [
      base,
      ...Object.keys(additional).map((key) => {
        return `[${dataAttr(key)}="${additional[key]}"]`
      }),
    ].join('')
  }

  return generateQueryString
}

const createGenerateLocator = ({ locator, testIdAttribute, map }: IGenerateLocatorsDataPayload) => {
  function generateLocator(): Record<string, Attribute>
  function generateLocator(suffix: Suffix, additional?: Attributes): Record<string, Attribute>
  function generateLocator(additional?: Attributes): Record<string, Attribute>
  function generateLocator(...args: unknown[]) {
    const suffix = typeof args[0] !== 'object' ? (args[0] as Suffix) : undefined

    const additional = (
      typeof args[1] !== 'undefined' ? args[1] : typeof args[0] === 'object' && args[0] !== null ? args[0] : {}
    ) as Attributes

    const locators = Object.keys(additional).reduce<Attributes>((memo, key) => {
      memo[dataAttr(key)] = additional[key]
      return memo
    }, {})

    return {
      [testIdAttribute]: toLocator({ map, locator, suffix }),
      ...locators,
    }
  }

  return generateLocator
}

export const toLocator = ({ map, locator, suffix }: { map: Map<string, string>; locator: string; suffix?: Suffix }) =>
  [map.get(locator), suffix].filter(Boolean).join('-')

export function createLocators<P extends string>({ name, componentLocators }: ICreateLocatorsArgs<P>) {
  return (props?: { namespace?: string }) => {
    const testIdAttribute = KEY
    const prefix = props && props.namespace ? props.namespace : name
    const map = componentLocators.reduce((memo, locator) => {
      memo.set(locator, [prefix, locator].filter(Boolean).join('-'))
      return memo
    }, new Map<string, string>())

    const locators = {} as Record<P, ReturnType<typeof createGenerateLocator>>

    const queryString = {} as Record<P, ReturnType<typeof createGenerateQueryString>>
    const testIds = {} as Record<P, (s?: Suffix) => string>
    componentLocators.forEach((locator) => {
      locators[locator] = createGenerateLocator({
        locator,
        testIdAttribute,
        map,
      })
      queryString[locator] = createGenerateQueryString({
        locator,
        testIdAttribute,
        map,
      })

      testIds[locator] = (suffix?: Suffix) => toLocator({ map, locator, suffix })
    })

    return {
      locators,
      testIds,
      queryString,
    }
  }
}
